import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil, finalize } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';

import { Client } from '@app/core/types/client';
import { IdentityDocument } from '@app/core/types/client-document';
import { IdentityReportsService } from '@app/core/services/identity-reports.service';
import { NotificationService } from '@app/core/services/notification.service';
import { BrandCodes } from '@app/core/types/brand';
import {
  DOCUMENT_STATE,
  DOCUMENT_STATE_CLASS,
  DOCUMENT_STATE_ICON,
  formatDocumentReason,
  ID_VERIFICATION_STATE,
  IdentityVerification,
  parseDocumentState,
  SlotKey,
  SLOT_CONFIGS,
  SLOT_KEYS,
  VERIFICATION_FLOW,
} from '@app/core/types/identity-verification';

interface SlotView {
  key: SlotKey;
  label: string;
  documentId: number | null;
  state: DOCUMENT_STATE;
  reason: string | null;
  reasonLabel: string;
  stateClass: 'valid' | 'invalid' | 'pending' | 'validating' | 'accepted';
  icon: string;
  actionable: boolean;
  canInvalidate: boolean;
}

@Component({
  selector: 'itfg-identity-verification-documents',
  templateUrl: './identity-verification-documents.component.html',
  styleUrl: './identity-verification-documents.component.scss',
  standalone: false,
})
export class IdentityVerificationDocumentsComponent
  implements OnInit, OnChanges, OnDestroy {
  public INVALIDATION_REASONS = [
    'LOW_QUALITY',
    'NO_CARD_DETECTED',
    'NOT_APPLICABLE',
    'NO_FACE_DETECTED',
    'MULTIPLE_FACES_DETECTED',
    'FACE_NOT_FULLY_VISIBLE',
    'FACE_MISMATCH',
    'DATA_MISMATCH',
    'EXPIRED',
    'UNDERAGE',
    'SCREENSHOT_DETECTED',
    'EDITED_DOCUMENT',
    'FRAUD',
    'OTHER',
  ] as const;
  public SLOT_CONFIGS = SLOT_CONFIGS;
  public SLOT_KEYS = SLOT_KEYS;

  @Input() client!: Client;

  detail: IdentityVerification | null | undefined = undefined;
  slotViews: SlotView[] = [];

  loadingOp: Record<SlotKey, boolean> = {
    front: false,
    back: false,
    selfie: false,
  };

  docList: IdentityDocument[] = [];
  loadingDocList = false;

  public _unsubscribe = new Subject<void>();

  constructor(
    private identityReportsService: IdentityReportsService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.identityReportsService.identityVerificationDetail$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((detail) => {
        this.detail = detail;
        this._rebuildSlotViews();
        if (detail !== null && detail !== undefined) {
          this._syncDocList();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['client'] &&
      this.client?.civilId &&
      changes['client'].currentValue?.civilId !==
      changes['client'].previousValue?.civilId
    ) {
      this._loadDocList();
    }
  }

  private _loadDocList(): void {
    this.loadingDocList = true;

    this.identityReportsService
      .getIdentityDocumentList$(
        this.client.civilId,
        this.client.brand.code as BrandCodes
      )
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingDocList = false))
      )
      .subscribe({
        next: (docs: IdentityDocument[]) => {
          this.docList = docs;
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardImageLoadError',
          });
        },
      });
  }

  private _syncDocList(): void {
    const referencedIds = SLOT_KEYS.map(
      (key) => this.detail?.[SLOT_CONFIGS[key].dtoKey] as number | null
    ).filter((id): id is number => !!id);

    const allPresent = referencedIds.every((id) =>
      this.docList.some((d) => d.id === id)
    );

    if (!allPresent) {
      this._loadDocList();
    }
  }

  private _rebuildSlotViews(): void {
    this.slotViews = [
      this._buildSlotView('selfie'),
      this._buildSlotView('front'),
      this._buildSlotView('back'),
    ]; // TODO dev-2545: better way to put the selfie first in the view!;
  }

  private _buildSlotView(key: SlotKey): SlotView {
    const cfg = SLOT_CONFIGS[key];
    const documentId = (this.detail?.[cfg.dtoKey] as number | null) ?? null;
    const parsed = parseDocumentState(
      (this.detail?.[cfg.stateKey] as string | null) ?? null
    );

    const stateClass = DOCUMENT_STATE_CLASS[parsed.base];
    const icon = DOCUMENT_STATE_ICON[parsed.base];

    const verificationState = this.detail?.state;
    const isVerificationActionable =
      verificationState === ID_VERIFICATION_STATE.IN_REVIEW ||
      verificationState === ID_VERIFICATION_STATE.IN_PROGRESS;
    const actionable = isVerificationActionable && stateClass !== 'pending';

    const isInternalFlow = this.detail?.flow === VERIFICATION_FLOW.INTERNAL;
    const canInvalidate = actionable && isInternalFlow;

    return {
      key,
      label: cfg.label,
      documentId,
      state: parsed.base,
      reason: parsed.reason,
      reasonLabel: parsed.reason ? formatDocumentReason(parsed.reason) : '',
      stateClass,
      icon,
      actionable,
      canInvalidate,
    };
  }

  // ── Document actions ───────────────────────────────────────────────

  downloadDocument(id: number, key: SlotKey): void {
    if (!id || !key) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .renderIdentityDocumentContent(this.client.civilId, id)
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingOp[key] = false))
      )
      .subscribe({
        next: (content: Blob) => {
          const blob = new Blob([content as any], { type: 'image/jpg' });
          saveAs(blob, `${key}-${id}.jpg`);
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardImageDownloadError',
          });
        },
      });
  }

  rotateDocument(id: number, key: SlotKey, clockwise: boolean): void {
    if (!id || !key) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .rotateIdentityDocument(this.client.civilId, id, clockwise)
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingOp[key] = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageRotateSuccessMessage',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardImageRotateError',
          });
        },
      });
  }

  deleteDocument(id: number, key: SlotKey): void {
    if (!id || !key) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .deleteIdentityDocument(this.client.civilId, id)
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingOp[key] = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageDeleteSuccessMessage',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardImageDeleteError',
          });
        },
      });
  }

  invalidateDocument(key: SlotKey, reason: string): void {
    if (!this.detail) return;

    this.loadingOp[key] = true;
    this.identityReportsService
      .invalidateVerificationDocument$(
        this.detail.id,
        SLOT_CONFIGS[key].apiType,
        reason
      )
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingOp[key] = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardStatusChangeSuccessMessage',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardStatusChangeError',
          });
        },
      });
  }

  uploadDocument(key: SlotKey, event: Event): void {
    if (!this.detail) {
      this.notificationService.showLocalizedErrorMessage({
        notificationText: 'verification.id.noVerificationContextError',
      });
      (event.target as HTMLInputElement).value = '';
      return;
    }

    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    this.loadingOp[key] = true;
    this.identityReportsService
      .addIdentityDocument$(
        this.client.civilId,
        this.client.brand.code as BrandCodes,
        SLOT_CONFIGS[key].apiType,
        formData
      )
      .pipe(
        takeUntil(this._unsubscribe),
        finalize(() => (this.loadingOp[key] = false))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'mi.idCardImageUploadSuccessMessage',
          });
          (event.target as HTMLInputElement).value = '';
        },
        error: (err: HttpErrorResponse) => {
          console.error(err);
          this.notificationService.showLocalizedErrorMessage({
            notificationText: 'mi.idCardImageUploadError',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
